import { useMemo } from "react";
import useSWR from "swr";
import { fetchAccountStats } from "lib/indexer/client";

export type AccountStats = {
  closedCount: number;
  cumsumCollateral: bigint;
  cumsumSize: bigint;
  losses: number;
  maxCapital: bigint;
  netCapital: bigint;
  realizedFees: bigint;
  realizedPnl: bigint;
  realizedPriceImpact: bigint;
  sumMaxSize: bigint;
  volume: bigint;
  wins: number;
  id: string;
};

export function useAccountStats(chainId: number, params: { account?: string; enabled?: boolean }) {
  const { account, enabled = true } = params;

  const { data, error, isLoading } = useSWR<AccountStats | undefined>(
    enabled && account ? ["useAccountStats", chainId, account] : null,
    async () => {
      const stats = await fetchAccountStats(account!).catch(() => null);
      if (!stats) return undefined;

      const volumeBigInt = BigInt(stats.volumeUsd);
      const pnlBigInt = BigInt(stats.realisedPnl);
      const feesBigInt = BigInt(stats.totalFeesPaid);

      return {
        closedCount: stats.totalTrades,
        cumsumCollateral: 0n,
        cumsumSize: volumeBigInt,
        losses: stats.losses,
        maxCapital: 0n,
        netCapital: 0n,
        realizedFees: feesBigInt,
        realizedPnl: pnlBigInt,
        realizedPriceImpact: 0n,
        sumMaxSize: volumeBigInt,
        volume: volumeBigInt,
        wins: stats.wins,
        id: stats.account,
      };
    }
  );

  return useMemo(() => ({ data, error, loading: isLoading }), [data, error, isLoading]);
}
